import React, { useState, useRef, useEffect } from 'react';
import { Button, Typography, message } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { AppDispatch, RootState } from '../../store';
import { setVerificationToken } from '../../store/slices/authSlice';
import { verifyOTP, forgotPassword } from '../../services/authService';
import AuthPageLayout from '../../components/auth/AuthPageLayout';
import AuthFormContainer, { Header } from '../../components/auth/AuthFormContainer';
import EmailSentModal from '../../components/auth/EmailSentModal';
import ButtonGroup from '../../components/common/ButtonGroup';

const { Title, Text } = Typography;

const OTPContainer = styled.div`
  margin-bottom: 32px;
`;

const Label = styled.label`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #1F2937;
  display: block;
  margin-bottom: 8px;
`;

const OTPInputWrapper = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 8px;
`;

const ErrorMessage = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 12px;
  color: #EB4335;
  margin-top: 8px;
`;

const OTPInput = styled.input`
  width: 56px;
  height: 56px;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  text-align: center;
  font-family: Inter;
  font-weight: 600;
  font-size: 24px;
  color: #1F2937;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: #2563EB;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &::placeholder {
    color: #D1D5DB;
  }
`;

const ResendContainer = styled.div`
  text-align: left;
  margin-top: 24px;
  font-family: Inter;
  font-weight: 600;
  font-size: 15px;
  color: #1F2937;

  button {
    background: none;
    border: none;
    color: #2563EB;
    font-family: Inter;
    font-weight: 600;
    font-size: 15px;
    cursor: pointer;
    padding: 0;
    margin-left: 4px;

    &:hover {
      text-decoration: underline;
    }

    &:disabled {
      color: #94A7CF;
      cursor: not-allowed;
      text-decoration: none;
    }
  }
`;

const OTPVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const email = location.state?.email || '';
  const verificationToken = useSelector((state: RootState) => state.auth.verificationToken);
  
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Redirect to forgot password if email or token missing
    if (!email || !verificationToken) {
      navigate('/auth/forgot-password');
      return;
    }
    // Focus first input on mount
    inputRefs.current[0]?.focus();
  }, [email, verificationToken, navigate]);

  useEffect(() => {
    // Countdown timer for resend
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [resendTimer]);

  const handleChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    // Clear error when user starts typing
    if (error) setError('');

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;
    const newOtp = [...otp];
    pastedData.split('').forEach((char, index) => {
      if (index < 6) {
        newOtp[index] = char;
      }
    });
    setOtp(newOtp);
    // Focus last filled input
    const lastIndex = Math.min(pastedData.length, 5);
    inputRefs.current[lastIndex]?.focus();
  };

  const handleClick = (index: number) => {
    // Find the first empty field
    const firstEmptyIndex = otp.findIndex((digit) => digit === '');
    
    // If there's an empty field before the clicked field, focus it instead
    if (firstEmptyIndex !== -1 && firstEmptyIndex < index) {
      inputRefs.current[firstEmptyIndex]?.focus();
    }
  };

  const handleSubmit = async () => {
    const code = otp.join('');
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }
    try {
      setLoading(true);
      setError('');
      await verifyOTP({
        email,
        verificationToken: verificationToken || '',
        verificationCode: code,
      });
      message.success('Code verified successfully');
      navigate('/auth/reset-password', { 
        state: { 
          email,
          verificationCode: code,
        } 
      });
    } catch (error: any) {
      // Case 1: Validation error (wrong code) - show inline error
      if (error?.code === 'VALIDATION_ERROR' && error?.data?.verificationCode) {
        setError(error.data.verificationCode[0] || 'Invalid code. Please try again.');
        return;
      }
      // Case 2: Other errors - show toast
      message.error(error?.message || 'Failed to verify code. Please try again.');
      console.error('Verify OTP failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setResendTimer(30);
      setError('');
      setOtp(['', '', '', '', '', '']);
      // Call forgot password API again to resend code
      const response = await forgotPassword({ email });
      // Update verificationToken in Redux
      dispatch(setVerificationToken(response.data.verificationToken));
      // Show success modal
      setShowModal(true);
      // Auto close modal and focus input
      setTimeout(() => {
        setShowModal(false);
        inputRefs.current[0]?.focus();
      }, 2000);
    } catch (error: any) {
      message.error(error?.message || 'Failed to resend code. Please try again.');
      console.error('Resend code failed:', error);
    }
  };

  const handleGoBack = () => {
    navigate('/auth/forgot-password');
  };

  return (
    <AuthPageLayout>
      <AuthFormContainer>
        <Header>
          <Title level={2}>One-time code</Title>
          <Text className="secondaryText">
            Please enter the code sent to {email}.
          </Text>
        </Header>

        <OTPContainer>
          <Label>Code</Label>
          <OTPInputWrapper>
            {otp.map((digit, index) => (
              <OTPInput
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onClick={() => handleClick(index)}
                onPaste={index === 0 ? handlePaste : undefined}
              />
            ))}
          </OTPInputWrapper>
          
          <ErrorMessage>{error}</ErrorMessage>

          <ResendContainer>
            Didn't receive the code?{' '}
            <button
              onClick={handleResend}
              disabled={resendTimer > 0}
            >
              {resendTimer > 0 ? `Resend code in: ${resendTimer}s` : 'Resend code'}
            </button>
          </ResendContainer>
        </OTPContainer>

        <ButtonGroup>
          <Button
            type='default'
            size='large'
            onClick={handleGoBack}
          >
            Go back
          </Button>

          <Button
            type='primary'
            size='large'
            onClick={handleSubmit}
            loading={loading}
            disabled={otp.join('').length !== 6 || loading}
          >
            Next
          </Button>
        </ButtonGroup>

        <EmailSentModal
          open={showModal}
          onClose={() => setShowModal(false)}
        />
      </AuthFormContainer>
    </AuthPageLayout>
  );
};

export default OTPVerificationPage;
